import axios from "axios";
import { IAxiosBlankReturn } from "../interface";
import { processAxiosError } from "../processAxiosError";

// Get CHUNK_SIZE from environment variable or default to 2MB
const CHUNK_SIZE_MB = parseInt(process.env.REACT_APP_CHUNK_SIZE_MB ?? "2");
const CHUNK_SIZE = 1024 * 1024 * CHUNK_SIZE_MB; // Convert MB to bytes

const MAX_PARALLEL_UPLOADS = parseInt(process.env.REACT_APP_MAX_PARALLEL_UPLOADS ?? "3");

export interface ILoadClinicalLibraryParams {
  hierarchyId: string;
  xmlText: string;
}

/**
 * Upload a clinical library XML to the backend in chunked requests.
 *
 * @param backEndUrl - Base backend URL
 * @param params - Parameters including hierarchyId and xmlText
 * @param controller - AbortController to cancel the upload
 * @param onProgress - Optional callback for upload progress (0-100)
 * @returns Standard IAxiosReturn indicating success or failure
 */
export const loadClinicalLibrary = async (
  backEndUrl: string,
  params: ILoadClinicalLibraryParams,
  controller: AbortController,
  onProgress?: (progressPercent: number) => void,
): Promise<IAxiosBlankReturn> => {
  /* Endpoint accepting chunked uploads (FormData with fileId, sequenceNumber, totalChunks, chunk). */
  const uploadChunkUrl = `${backEndUrl}ClinicalLibrary/UploadChunks`;
  /* Endpoint to signal completion of all chunk uploads (FormData with fileId, hierarchyId). */
  const completeUploadUrl = `${backEndUrl}ClinicalLibrary/CompleteUpload`;

  try {
    // Convert XML string to Uint8Array for chunking
    const encoder = new TextEncoder();
    /* UTF-8 encoded byte representation of the XML text.*/
    const xmlData = encoder.encode(params.xmlText);

    // Calculate chunks
    const totalChunks = Math.ceil(xmlData.length / CHUNK_SIZE);
    /**
     * Unique identifier used to associate all chunks and the completion request.
     * This must be stable across all requests in a single upload.
     */
    const fileId = crypto.randomUUID();
    let uploadedChunks = 0;

    // Upload chunks in small parallel batches
    for (let i = 0; i < totalChunks; i += MAX_PARALLEL_UPLOADS) {
      const batch = Array.from(
        { length: Math.min(MAX_PARALLEL_UPLOADS, totalChunks - i) },
        (_, j) => i + j,
      );

      await Promise.all(
        batch.map(async (sequenceNumber) => {
          const start = sequenceNumber * CHUNK_SIZE;
          const end = Math.min((sequenceNumber + 1) * CHUNK_SIZE, xmlData.length);
          const chunkData = xmlData.slice(start, end);
          const chunkBlob = new Blob([chunkData], { type: "application/xml" });

          const formData = new FormData();
          formData.append("fileId", fileId);
          formData.append("sequenceNumber", sequenceNumber.toString());
          formData.append("totalChunks", totalChunks.toString());
          formData.append("hierarchyId", params.hierarchyId);
          formData.append("chunk", chunkBlob, `chunk_${sequenceNumber}.xml`);

          await axios.post(uploadChunkUrl, formData, { signal: controller.signal });
          uploadedChunks++;
          onProgress?.(Math.round((uploadedChunks / totalChunks) * 100));
        }),
      );
    }
    // Notify backend all chunks are uploaded
    const completeForm = new FormData();
    completeForm.append("fileId", fileId);
    completeForm.append("hierarchyId", params.hierarchyId);

    await axios.post(completeUploadUrl, completeForm, { signal: controller.signal });
    onProgress?.(100);
    return { success: true };
  } catch (error: unknown) {
    return processAxiosError("Load clinical library in chunks failed", error);
  }
};
